
import { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Text, Select, Button, HStack, useDisclosure,
} from "@chakra-ui/react";
import { ViewIcon } from "@chakra-ui/icons";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import DateRangeFilter from "components/DateRangeFilter/DateRangeFilter";
import AgGridWrapper from "components/AgGrid/AgGridWrapper";
import StoreDetailModal from "./StoreDetailModal";
import GridPagination from "components/CustomPagination/GridPagination";
import { generatePresignedUrl } from "Utils/s3Utils";


const RED = "#C0392B";
const BORDER = "#EBEBEB";
// ─── Action renderer ──────────────────────────────────────────────────────────
const ActionRenderer = ({ data, context }) => (
  <Button
    size="sm"
    variant="outline"
    colorScheme="red"
    leftIcon={<ViewIcon />}
    borderRadius="6px"
    fontSize="12px"
    fontWeight={600}
    h="30px"
    px={3}
    _hover={{ bg: RED, color: "white", borderColor: RED }}
    onClick={() => context?.onViewDetails?.(data)}
  >
    View
  </Button>
);

// ─── Column definitions ───────────────────────────────────────────────────────
const buildColumnDefs = () => [
  {
    headerName: "Store ID",
    field: "storeId",
    minWidth: 120,
    maxWidth: 160,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { fontWeight: 600, color: "#2D3748", display: "flex", alignItems: "center" },
  },
  {
    headerName: "Store Name",
    field: "storeName",
    minWidth: 200,
    flex: 2,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { color: "#1A202C", display: "flex", alignItems: "center" },
  },
  {
    headerName: "Store Type",
    field: "storeType",
    minWidth: 150,
    flex: 1,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Store Class",
    field: "storeCls",           
    minWidth: 140,
    flex: 1,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Action",
    field: "action",
    minWidth: 110,
    maxWidth: 130,
    sortable: false,
    filter: false,
    resizable: false,
    pinned: "right",
    cellRenderer: ActionRenderer,
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
  },
];

// ─── Main component ───────────────────────────────────────────────────────────
export default function ModalResults() {
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate]                   = useState(today);
  const [toDate, setToDate]                       = useState(today);
  const [iterations, setIterations]               = useState([]);
  const [iterationsLoading, setIterationsLoading] = useState(false);
  const [selectedIteration, setSelectedIteration] = useState("");
  const [stores, setStores]                       = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState("");
  const [selectedStore, setSelectedStore]         = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const columnDefs = buildColumnDefs();
  const [productLoading, setProductLoading] = useState(false);
  const [imageUrls, setImageUrls]           = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 10; 

 const handlePrevious = () => setCurrentPage((p) => p - 1);
 const handleNext     = () => setCurrentPage((p) => p + 1);
 const handleGoToPage = (page) => { // page is 1-based
   const totalPages = Math.ceil(stores.length / PAGE_SIZE);
   setCurrentPage(Math.min(Math.max(page - 1, 0), totalPages - 1));
 };
  const fetchStores = useCallback(async (iterationId) => {
    if (!iterationId) return;
    setLoading(true);
    setStores([]);
    setCurrentPage(0);
    setError("");
    try {
      const res = await apiClient.get(
        `${API_CONFIG.ENDPOINTS.MODAL_RESULTS.STORES}?iterationId=${iterationId}` 
      );
      setStores(res.data);
    } catch {
      setError("Failed to load stores. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch iterations when dates change ──────────────────────────────────────
  useEffect(() => {
    if (!fromDate || !toDate) return;

    const fetchIterations = async () => {
      setIterationsLoading(true);
      setIterations([]);
      setSelectedIteration("");
      setStores([]);
      setError("");
      try {
        const [fromYear, fromMonth, fromDay] = fromDate.split("-").map(Number);
        const [toYear,   toMonth,   toDay  ] = toDate.split("-").map(Number);

        const res = await apiClient.post(
          API_CONFIG.ENDPOINTS.MODAL_RESULTS.ITERATIONS,
          {
            from: { date: fromDay,  month: fromMonth, year: fromYear },
            to:   { date: toDay,    month: toMonth,   year: toYear   },
          }
        );

        setIterations(res.data);
        if (res.data?.length > 0) {
          const first = res.data[0];
          setSelectedIteration(first);
          await fetchStores(first);  
        }

      } catch {
        setError("Failed to load iterations. Please try again.");
      } finally {
        setIterationsLoading(false);
      }
    };

    fetchIterations();
  }, [fromDate, toDate, fetchStores]);

  const handleIterationChange = (e) => {
    const val = e.target.value;
    setSelectedIteration(val);
    setCurrentPage(0);
    fetchStores(val);
  };

  // ── View details ─────────────────────────────────────────────────────────────
  const handleViewDetails = useCallback(async (store) => {
  setSelectedStore(store);
  setImageUrls([]); 
  onOpen();
  setProductLoading(true);
  try {
    const res = await apiClient.get(
      `${API_CONFIG.ENDPOINTS.MODAL_RESULTS.PRODUCT_DETAILS}?iterationId=${selectedIteration}&storeId=${store.storeId}`
    );
   

    if (res.data?.length > 0) {
      const urlResults = await Promise.all( 
        res.data.map(async(img)=>{
          const [actualUrl, annotatedUrl] = await Promise.all([
            generatePresignedUrl(img.actualFilePath),
            generatePresignedUrl(img.annotatedFilePath),
          ]);
          return {
            fileName:  img.ImageFileName,
            actual:    actualUrl,
            annotated: annotatedUrl,
            products:img.products,
          };
        })
      );

      setImageUrls(urlResults); 
    }
  } catch {
    setError("Failed to load product details.");
  } finally {
    setProductLoading(false);
  }
}, [onOpen, selectedIteration]);

  const gridContext = { onViewDetails: handleViewDetails };

  return (
    <Box
      minH="100vh" bg="gray.50"
      py={{ base: 6, md: 10 }}
      px={{ base: 3, sm: 4, md: 8 }}
      fontFamily="'Segoe UI', sans-serif"
    >
      {/* ── Filter bar ── */}
      <Flex
        align={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap={3} mb={8} mt={6}
        justifyContent="space-between"
      >
        <HStack spacing={3}>
          <Text fontWeight={600} fontSize="sm" color="gray.600" whiteSpace="nowrap">
            Iteration ID:
          </Text>
          <Select
            placeholder="-- Select Iteration --"
            value={selectedIteration}
            onChange={handleIterationChange}
            isDisabled={iterationsLoading || iterations.length === 0}
            maxW={{ base: "100%", sm: "220px" }}
            borderColor={BORDER}
            borderRadius="8px"
            fontSize="sm"
            focusBorderColor={RED}
            _hover={{ borderColor: RED }}
            bg="white"
            size="md"
          >
            {iterations.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </Select>
        </HStack>

        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
        />
      </Flex>

      {/* ── Error ── */}
      {error && (
        <Text color="red.500" fontSize="sm" mb={4} fontWeight={500}>
          {error}
        </Text>
      )}

      {/* ── AG Grid ── */}
      <Box
        bg="white" borderRadius="12px"
        border={`2px solid ${BORDER}`}
        overflow="hidden" boxShadow="sm"
      >
        <AgGridWrapper
          rowData={stores}               
          columnDefs={columnDefs}
          loading={loading}              
          height="590px"
          extraGridProps={{ context: gridContext }}
          noRowsMessage={
            !fromDate || !toDate
              ? "Select a date range to load iterations."
              : !selectedIteration
              ? "Select an Iteration ID to load store data."
              : "No stores found for this iteration."
          }
        />
      </Box>
      {stores.length > 0 && (
      <GridPagination
        currentPage={currentPage}
        totalPages={Math.ceil(stores.length / PAGE_SIZE)}
        totalElements={stores.length}
        pageSize={PAGE_SIZE}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onGoToPage={handleGoToPage}
      />
)}
      <StoreDetailModal
        isOpen={isOpen}
        onClose={onClose}
        selectedStore={selectedStore}
        productLoading={productLoading}
        imageUrls={imageUrls}
      />
      
    </Box>
  );
}